import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Select, {ClassNamesConfig, FormatOptionLabelMeta, StylesConfig} from "react-select";
import {CountryCode, parsePhoneNumberWithError} from "libphonenumber-js";
import {countries} from "../../lib/countries";
import {FieldError} from "./FieldError";
import {GolemErrorState} from "./useGolemErrors";

// ── Types ─────────────────────────────────────────────────────────────────────

type CountryOption = {
    value: string;
    label: string;
    shortLabel: string;
    callingCode: string;
};

type GolemPhoneInputProps = {
    label?: string;
    initialValue?: string;
    onChange: (value: string) => void;
    errors: GolemErrorState;
    field: string;
    disabled?: boolean;
    required?: boolean;
};

// ── Styles ────────────────────────────────────────────────────────────────────

const controlClass = (error: boolean, disabled: boolean) =>
    `flex h-10 w-full rounded-md shadow-md transition-all ${
        error ? "outline outline-2 outline-red -outline-offset-2" : "outline-none"
    } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`;

const selectStyles: StylesConfig<CountryOption, false> = {
    control: (base) => ({...base, border: "none", boxShadow: "none", height: "100%", minHeight: 0, borderRadius: 0, backgroundColor: "transparent"}),
    menu: (base) => ({...base, width: 320, zIndex: 50}),
    valueContainer: (base) => ({...base, padding: "0 8px", marginLeft: 0}),
    indicatorSeparator: () => ({display: "none"}),
    dropdownIndicator: (base) => ({...base, padding: "0 4px"}),
};

const selectClassNames: ClassNamesConfig<CountryOption, false> = {
    control: () => "h-full w-full",
};

// ── Sub-component: country select ─────────────────────────────────────────────

const countryOptions: CountryOption[] = countries.map((c) => ({
    value: c.countryCode,
    callingCode: c.countryCallingCode,
    label: `${c.flag}  +${c.countryCallingCode}  ${c.countryNameEn}`,
    shortLabel: `${c.flag} +${c.countryCallingCode}`,
}));

function formatOptionLabel(option: CountryOption, meta: FormatOptionLabelMeta<CountryOption>) {
    if (meta.context === "menu") {
        return (
            <div className="grid grid-cols-[28px_52px_1fr] gap-1 text-sm">
                <span>{option.value && countries.find(c => c.countryCode === option.value)?.flag}</span>
                <span>+{option.callingCode}</span>
                <span>{countries.find(c => c.countryCode === option.value)?.countryNameEn}</span>
            </div>
        );
    }
    return <span className="text-sm whitespace-nowrap">{option.shortLabel}</span>;
}

// ── Main component ────────────────────────────────────────────────────────────

export function GolemPhoneInput({label, initialValue, onChange, errors, field, disabled, required}: GolemPhoneInputProps) {
    const error = errors.fieldError(field);

    const [countryCode, setCountryCode] = useState<string>("");
    const [callingCode, setCallingCode] = useState<string>("");
    const [number, setNumber] = useState<string>("");
    const [initialised, setInitialised] = useState(false);

    // Parse initialValue once on mount
    useEffect(() => {
        if (initialised) return;
        if (initialValue) {
            try {
                const parsed = parsePhoneNumberWithError(initialValue);
                if (parsed?.isValid()) {
                    setCountryCode(parsed.country ?? "");
                    setCallingCode(String(parsed.countryCallingCode));
                    setNumber(parsed.nationalNumber);
                } else {
                    setNumber(initialValue);
                }
            } catch {
                setNumber(initialValue);
            }
        }
        setInitialised(true);
    }, [initialValue]);

    const selectedOption = useMemo(
        () => countryOptions.find((o) => o.value === countryCode) ?? null,
        [countryCode],
    );

    const handleCountryChange = useCallback((option: CountryOption | null) => {
        errors.clearField(field);
        setCountryCode(option?.value ?? "");
        setCallingCode(option?.callingCode ?? "");
    }, [errors, field]);

    // Emit E164 whenever country or number changes
    useEffect(() => {
        if (!initialised) return;
        if (!number.trim()) {
            onChange("");
            return;
        }
        const candidates = [`+${callingCode}${number}`, number];
        for (const candidate of candidates) {
            try {
                const parsed = parsePhoneNumberWithError(candidate, countryCode as CountryCode);
                if (parsed.isValid()) {
                    onChange(parsed.format("E.164"));
                    return;
                }
            } catch {}
        }
        onChange(`+${callingCode}${number}`);
    }, [callingCode, number, initialised]);

    const handleNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        errors.clearField(field);
        setNumber(e.target.value);
    }, [errors, field]);

    return (
        <div>
            {label != null && (
                <label className={`mb-1 block ${error ? "text-red" : ""}`}>
                    {label}
                    {required && <span className="text-dark-red"> *</span>}
                </label>
            )}
            <div className={controlClass(!!error, !!disabled)}>
                <div className="w-[140px] shrink-0 border-r border-light-light-grey self-stretch flex items-center overflow-hidden rounded-l-md bg-white mx-0.5">
                    {initialised && (
                        <Select<CountryOption, false>
                            className="w-full mr-0.5"
                            options={countryOptions}
                            value={selectedOption}
                            onChange={handleCountryChange}
                            formatOptionLabel={formatOptionLabel}
                            styles={selectStyles}
                            classNames={selectClassNames}
                            isDisabled={disabled}
                            placeholder="Country"
                            menuPortalTarget={document.body}
                        />
                    )}
                </div>
                <input
                    type="text"
                    className="flex-1 px-3 text-sm bg-transparent outline-none disabled:cursor-not-allowed"
                    value={number}
                    onChange={handleNumberChange}
                    disabled={disabled}
                    placeholder="Phone number"
                />
            </div>
            <FieldError error={error}/>
        </div>
    );
}
